import { useId, useRef, useState } from "react";
import type { ClipboardEvent, InputHTMLAttributes, KeyboardEvent, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { normalizeOtp, OTP_LENGTH } from "../lib/otp.js";
import { estimateStrength } from "../lib/strength.js";

const CONTROL_BASE =
  "w-full rounded-lg border bg-ink px-3 text-sm text-foam placeholder:text-mist-2 transition-colors duration-150 focus:border-flow/70 focus:outline-none focus:ring-2 focus:ring-flow/20 disabled:opacity-50";
const CONTROL_IDLE = "border-line-strong hover:border-foam/30";
const CONTROL_INVALID = "border-buoy/60 focus:border-buoy/70 focus:ring-buoy/10";

export function describedBy(id: string, hint?: string | undefined, error?: string | undefined): string | undefined {
  const ids: string[] = [];
  if (hint !== undefined && hint !== "") {
    ids.push(`${id}-hint`);
  }
  if (error !== undefined && error !== "") {
    ids.push(`${id}-error`);
  }
  return ids.length === 0 ? undefined : ids.join(" ");
}

export function Field(props: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  const hasError = props.error !== undefined && props.error !== "";
  const hasHint = props.hint !== undefined && props.hint !== "";
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={props.id} className="text-[0.8125rem] font-medium text-foam">
          {props.label}
        </label>
        {props.action}
      </div>
      {props.children}
      {hasError ? (
        <p id={`${props.id}-error`} className="text-[0.8125rem] text-buoy">
          {props.error}
        </p>
      ) : hasHint ? (
        <p id={`${props.id}-hint`} className="text-[0.8125rem] text-mist-2">
          {props.hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly invalid?: boolean | undefined;
}

export function TextInput(props: TextInputProps): ReactNode {
  const { invalid = false, className = "", ...rest } = props;
  return (
    <input
      {...rest}
      aria-invalid={invalid || undefined}
      className={`${CONTROL_BASE} h-10 ${invalid ? CONTROL_INVALID : CONTROL_IDLE} ${className}`}
    />
  );
}

export function SecretInput(props: TextInputProps & { readonly revealLabel?: string | undefined }): ReactNode {
  const [revealed, setRevealed] = useState(false);
  const { revealLabel = "Passwort", className = "", invalid = false, ...rest } = props;
  return (
    <div className="relative">
      <input
        {...rest}
        type={revealed ? "text" : "password"}
        spellCheck={false}
        autoCapitalize="none"
        aria-invalid={invalid || undefined}
        className={`${CONTROL_BASE} h-10 pr-11 ${invalid ? CONTROL_INVALID : CONTROL_IDLE} ${className}`}
      />
      <button
        type="button"
        aria-label={revealed ? `${revealLabel} verbergen` : `${revealLabel} anzeigen`}
        aria-pressed={revealed}
        onClick={(): void => setRevealed((value: boolean): boolean => !value)}
        className="absolute right-1 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-md text-mist transition-colors hover:bg-foam/[0.06] hover:text-foam"
      >
        {revealed ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
      </button>
    </div>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean | undefined;
}

export function TextArea(props: TextAreaProps): ReactNode {
  const { invalid = false, className = "", ...rest } = props;
  return (
    <textarea
      {...rest}
      aria-invalid={invalid || undefined}
      className={`${CONTROL_BASE} min-h-24 resize-y py-2.5 leading-relaxed ${invalid ? CONTROL_INVALID : CONTROL_IDLE} ${className}`}
    />
  );
}

export function SelectInput(props: SelectHTMLAttributes<HTMLSelectElement>): ReactNode {
  const { className = "", children, ...rest } = props;
  return (
    <div className="relative">
      <select
        {...rest}
        className={`${CONTROL_BASE} h-10 appearance-none pr-9 ${CONTROL_IDLE} ${className}`}
      >
        {children}
      </select>
      <ChevronDown size={16} aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-mist" />
    </div>
  );
}

export function Switch(props: {
  readonly label: string;
  readonly description?: string | undefined;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean | undefined;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-5 py-1">
      <div className="min-w-0">
        <label htmlFor={id} className="block cursor-pointer text-sm font-medium text-foam">
          {props.label}
        </label>
        {props.description !== undefined ? <p className="mt-0.5 text-sm text-mist-2">{props.description}</p> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={props.checked}
        disabled={props.disabled}
        onClick={(): void => props.onChange(!props.checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border transition-colors duration-150 disabled:opacity-40 ${
          props.checked ? "border-flow bg-flow" : "border-line-strong bg-ink-4"
        }`}
      >
        <span
          className={`absolute top-[2px] size-3.5 rounded-full transition-transform duration-150 ease-flow ${
            props.checked ? "translate-x-[18px] bg-ink" : "translate-x-[2px] bg-mist"
          }`}
        />
      </button>
    </div>
  );
}

export function Checkbox(props: {
  readonly id: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <label htmlFor={props.id} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-mist">
      <input
        id={props.id}
        type="checkbox"
        checked={props.checked}
        onChange={(event): void => props.onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-[#62F0D8]"
      />
      <span>{props.children}</span>
    </label>
  );
}

export interface OtpInputProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onComplete?: ((value: string) => void) | undefined;
  readonly disabled?: boolean | undefined;
  readonly autoFocus?: boolean | undefined;
  readonly invalid?: boolean | undefined;
}

export function OtpInput(props: OtpInputProps): ReactNode {
  const cells = useRef<Array<HTMLInputElement | null>>([]);

  function focusCell(index: number): void {
    cells.current[Math.min(Math.max(index, 0), OTP_LENGTH - 1)]?.focus();
  }

  function commit(next: string): void {
    props.onChange(next);
    if (next.length === OTP_LENGTH) {
      props.onComplete?.(next);
    }
  }

  function writeDigit(index: number, digit: string): void {
    const at = Math.min(index, props.value.length);
    const next = props.value.slice(0, at) + digit + props.value.slice(at + 1);
    commit(next.slice(0, OTP_LENGTH));
    focusCell(at + 1);
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (index < props.value.length) {
        commit(props.value.slice(0, index) + props.value.slice(index + 1));
        focusCell(index);
      } else if (index > 0) {
        commit(props.value.slice(0, index - 1) + props.value.slice(index));
        focusCell(index - 1);
      }
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusCell(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      focusCell(index + 1);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const digits = normalizeOtp(event.clipboardData.getData("text"));
    if (digits === "") {
      return;
    }
    event.preventDefault();
    commit(digits);
    focusCell(Math.min(digits.length, OTP_LENGTH - 1));
  }

  return (
    <div role="group" aria-label={props.label} className="flex justify-center gap-2">
      {Array.from({ length: OTP_LENGTH }, (_, index: number): ReactNode => {
        const digit = props.value.charAt(index);
        return (
          <input
            key={index}
            ref={(el: HTMLInputElement | null): void => {
              cells.current[index] = el;
            }}
            value={digit}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            aria-label={`Ziffer ${index + 1} von ${OTP_LENGTH}`}
            autoFocus={props.autoFocus === true && index === 0}
            disabled={props.disabled}
            aria-invalid={props.invalid || undefined}
            onFocus={(event): void => event.target.select()}
            onChange={(event): void => {
              const digitOnly = normalizeOtp(event.target.value);
              if (digitOnly !== "") {
                writeDigit(index, digitOnly.charAt(0));
              }
            }}
            onKeyDown={(event): void => onKeyDown(index, event)}
            onPaste={onPaste}
            className={`font-mono h-12 w-10 rounded-lg border bg-ink text-center text-xl font-semibold text-foam transition-colors duration-150 focus:border-flow/70 focus:outline-none focus:ring-2 focus:ring-flow/20 disabled:opacity-50 sm:h-14 sm:w-12 ${
              props.invalid === true ? CONTROL_INVALID : digit !== "" ? "border-flow/40" : CONTROL_IDLE
            }`}
          />
        );
      })}
    </div>
  );
}

export function StrengthMeter(props: { readonly password: string }): ReactNode {
  if (props.password === "") {
    return null;
  }
  const report = estimateStrength(props.password);
  const filled = Math.min(4, Math.max(1, report.level));
  const color = report.level <= 1 ? "bg-buoy" : report.level === 2 ? "bg-mist" : "bg-flow";
  return (
    <div className="flex items-center gap-3" aria-live="polite">
      <div className="flex flex-1 gap-1.5" aria-hidden="true">
        {[1, 2, 3, 4].map((segment: number): ReactNode => (
          <span
            key={segment}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${segment <= filled ? color : "bg-ink-4"}`}
          />
        ))}
      </div>
      <span className="shrink-0 text-xs text-mist">
        {report.label} · ca. {report.bits} Bit
      </span>
    </div>
  );
}

function glyphClass(char: string): string {
  if (/[0-9]/.test(char)) {
    return "text-flow";
  }
  if (/[a-zA-Z]/.test(char)) {
    return "text-foam";
  }
  return "text-amber-300";
}

/** Monospace secret with digits and symbols tinted, so look-alike characters are easy to tell apart. */
export function PasswordGlyphs(props: { readonly value: string; readonly className?: string | undefined }): ReactNode {
  return (
    <span className={`font-mono break-all leading-relaxed ${props.className ?? ""}`}>
      {Array.from(props.value).map((char: string, index: number) => (
        <span key={index} className={glyphClass(char)}>
          {char}
        </span>
      ))}
    </span>
  );
}
